/**
 * TEMPORARY: Knowledge Hub articles are read from the CMS, because the Ecommerce Search
 * content scope only returns id, path, name and short description today. Once it also
 * exposes image, publication date, author and the category facets, this whole file is
 * deleted and the BlogOverview block asks the content search instead.
 *
 * It is kept apart from the general content queries in ./cms.ts on purpose, so that
 * removing it cannot affect anything else.
 */
import { Builder, builder } from '@builder.io/react';
import { BuilderContent } from '@builder.io/sdk';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/api/apiConfig';
import { DamFileIdentifier } from '@/contexts/DamFileProvider';
import { SettingsResponse } from '@/generated/data.schemas';
import { CmsArticle } from '@/types/article';
import { CmsAuthor } from '@/types/author';

/**
 * Only the fields the article cards need, so an article does not drag its whole block
 * tree along. The author is a reference, and only stays enriched when its path is named.
 *
 * The filter categories are deliberately not asked for: they are only used to select
 * articles, which happens in the query, and enriching them would pull a full category
 * entry per reference into every card.
 */
const articleFields = [
	'id',
	'data.url',
	'data.headline',
	'data.title',
	'data.description',
	'data.shortDescription',
	'data.featuredImage',
	'data.publicationDate',
	'data.readingTime',
	'data.visibleCardLabels',
	'data.author.value.data',
].join(',');

/** The first value an editor actually filled in, so an empty field falls through */
const firstFilledIn = (...values: readonly unknown[]): string | undefined => {
	for (const value of values) {
		if (typeof value === 'string' && value.trim().length > 0) {
			return value;
		}
	}

	return undefined;
};

/**
 * The category names off `data.filterCategories`, where each item wraps its reference in a
 * `category` field and an enriched reference carries the entry under `value.data`.
 *
 * Empty for a card, whose projection leaves the references out, and filled in wherever the
 * whole page is read. An unenriched or dangling reference simply drops out.
 */
const toCategoryNames = (value: unknown): string[] | undefined => {
	if (!Array.isArray(value)) {
		return undefined;
	}

	const names = value
		.map((item) => (item as { category?: { value?: { data?: { name?: unknown } } } })?.category?.value?.data?.name)
		.filter((name): name is string => typeof name === 'string' && name.trim().length > 0);

	return names.length > 0 ? names : undefined;
};

const toStringArray = (value: unknown): string[] | undefined => {
	if (Array.isArray(value)) {
		return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
	}
	// The category fields started out single valued, so a plain string is still tolerated
	if (typeof value === 'string' && value.length > 0) {
		return [value];
	}
	return undefined;
};

export const mapCmsArticle = (entry: BuilderContent): CmsArticle => {
	const data = entry.data ?? {};

	return {
		id: entry.id ?? '',
		url: typeof data.url === 'string' ? data.url : undefined,
		// Falls back to the page title, which is the meta title and so reads with the site
		// name appended
		headline: firstFilledIn(data.headline, data.title),
		// The page description is the meta description, written for the same article
		shortDescription: firstFilledIn(data.shortDescription, data.description),
		featuredImage: (data.featuredImage as DamFileIdentifier | undefined) ?? undefined,
		publicationDate: typeof data.publicationDate === 'number' ? data.publicationDate : undefined,
		readingTime: typeof data.readingTime === 'string' ? data.readingTime : undefined,
		categories: toCategoryNames(data.filterCategories),
		visibleCardLabels: toStringArray(data.visibleCardLabels),
		author: (data.author?.value?.data as CmsAuthor | undefined) ?? undefined,
	};
};

/**
 * Asked of the content API directly instead of through builder.getAll.
 *
 * Every page entry is targeted at its own `urlPath`, and the SDK always sends user
 * attributes — falling back to the current `urlPath` when none are given, and sharing
 * whatever the first queued request supplied when several run at once. Either way the
 * CMS then applies that targeting, and a list query spanning many articles matches
 * nothing. Sending no user attributes at all is the only way to get a list back.
 */
/** The most the content API returns in one call. Paging becomes Ecommerce Search's job. */
const articlesLimit = 100;

const fetchKnowledgeHubArticles = async (params: {
	locale: string;
	modelName: string;
	categoryIds: readonly string[];
}): Promise<CmsArticle[]> => {
	if (params.categoryIds.length === 0 || params.modelName.length === 0 || !builder.apiKey) {
		return [];
	}

	const url = new URL(`https://cdn.builder.io/api/v3/content/${params.modelName}`);
	url.searchParams.set('apiKey', builder.apiKey);
	url.searchParams.set('limit', String(articlesLimit));
	url.searchParams.set('fields', articleFields);
	url.searchParams.set('enrich', 'true');
	// Each filterCategories item wraps its reference in a `category` field,
	// so the id sits one level deeper than the field itself
	url.searchParams.set(
		'query',
		JSON.stringify({
			'data.filterCategories.category.id': { $in: [...params.categoryIds] },
			// An article opts out of every overview by turning this on. A page that has never
			// carried the field is not excluded, so this is harmless before any editor sets it.
			// The same field becomes the filter the Ecommerce Search index applies later.
			'data.hideInOverview': { $ne: true },
		}),
	);
	if (params.locale.length > 0) {
		url.searchParams.set('locale', params.locale);
	}
	// Editors need to see their newest articles straight away, visitors can have the cached list
	if (Builder.isEditing) {
		url.searchParams.set('cachebust', 'true');
	}

	try {
		const response = await fetch(url.toString());
		if (!response.ok) {
			throw new Error(`Content API answered ${response.status}`);
		}

		const payload = (await response.json()) as { readonly results?: BuilderContent[] };
		return (payload.results ?? []).map(mapCmsArticle);
	} catch (error) {
		console.error(`Error fetching knowledge hub articles for locale ${params.locale}`, error);
	}

	return [];
};

export const useKnowledgeHubArticlesQuery = (
	settings: SettingsResponse | undefined,
	params: {
		categoryIds: readonly string[];
	},
): UseQueryResult<CmsArticle[]> => {
	const cultureCode = settings?.cultureCode ?? '';
	const modelName = settings?.cmsSettings.page ?? '';

	return useQuery({
		queryKey: queryKeys.cms.knowledgeHubArticles({
			locale: cultureCode,
			modelName,
			categoryIds: params.categoryIds,
		}).queryKey,
		queryFn: () =>
			fetchKnowledgeHubArticles({
				locale: cultureCode,
				modelName,
				categoryIds: params.categoryIds,
			}),
		enabled: !!settings && modelName.length > 0 && params.categoryIds.length > 0,
	});
};

/**
 * The one article lifted above the overview.
 *
 * A picked article is found through `urlPath` targeting rather than a query on `data.url`,
 * which matches nothing: every page entry is targeted at its own path, and that targeting is
 * how the CMS resolves a single page. With nothing picked, the newest article in the same
 * categories the grid shows stands in. That is a list query, so it must carry no targeting.
 */
const fetchFeaturedArticle = async (params: {
	locale: string;
	modelName: string;
	path: string;
	categoryIds: readonly string[];
}): Promise<CmsArticle | null> => {
	const hasPath = params.path.length > 0;
	if (params.modelName.length === 0 || !builder.apiKey || (!hasPath && params.categoryIds.length === 0)) {
		return null;
	}

	const url = new URL(`https://cdn.builder.io/api/v3/content/${params.modelName}`);
	url.searchParams.set('apiKey', builder.apiKey);
	url.searchParams.set('limit', '1');
	url.searchParams.set('fields', articleFields);
	url.searchParams.set('enrich', 'true');

	if (hasPath) {
		url.searchParams.set('userAttributes', JSON.stringify({ urlPath: params.path }));
	} else {
		url.searchParams.set(
			'query',
			JSON.stringify({
				'data.filterCategories.category.id': { $in: [...params.categoryIds] },
				// A hidden article should not be the one that stands in. A picked article still
				// shows, since an editor choosing it outranks it being kept out of the grid.
				'data.hideInOverview': { $ne: true },
			}),
		);
		// Newest first. An article with no publication date cannot be the most recently
		// published one, and a sorted result leaves it out anyway.
		url.searchParams.set('sort.data.publicationDate', '-1');
	}

	if (params.locale.length > 0) {
		url.searchParams.set('locale', params.locale);
	}
	if (Builder.isEditing) {
		url.searchParams.set('cachebust', 'true');
	}

	const response = await fetch(url.toString());
	if (!response.ok) {
		throw new Error(`Content API answered ${response.status}`);
	}

	const payload = (await response.json()) as { readonly results?: BuilderContent[] };
	const entry = (payload.results ?? [])[0];
	return entry ? mapCmsArticle(entry) : null;
};

export const useFeaturedArticleQuery = (
	settings: SettingsResponse | undefined,
	params: {
		path: string;
		categoryIds: readonly string[];
	},
): UseQueryResult<CmsArticle | null> => {
	const cultureCode = settings?.cultureCode ?? '';
	const modelName = settings?.cmsSettings.page ?? '';

	return useQuery({
		queryKey: queryKeys.cms.knowledgeHubFeaturedArticle({
			locale: cultureCode,
			modelName,
			path: params.path,
			categoryIds: params.categoryIds,
		}).queryKey,
		queryFn: async () => {
			try {
				return await fetchFeaturedArticle({
					locale: cultureCode,
					modelName,
					path: params.path,
					categoryIds: params.categoryIds,
				});
			} catch (error) {
				console.error(`Error fetching the featured article for locale ${cultureCode}`, error);
				return null;
			}
		},
		enabled: !!settings && modelName.length > 0,
	});
};
